import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/context/OptionsContext";

const Ctx = createContext(null);
export const useMessaging = () => useContext(Ctx);

export const MessagingProvider = ({ children }) => {
  const { user } = useAuth();
  const { reload: reloadOptions } = useOptions();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [othersLastRead, setOthersLastRead] = useState("");
  const [typing, setTyping] = useState({});
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const activeRef = useRef(null);
  const reconnectRef = useRef(null);
  activeRef.current = activeId;

  const loadConversations = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setConversations(data); } catch {}
  }, []);

  const loadMessages = useCallback(async (cid) => {
    try {
      const { data } = await api.get(`/conversations/${cid}/messages`);
      setMessages(data.messages || []);
      setOthersLastRead(data.others_last_read || "");
    } catch {}
  }, []);

  const markRead = useCallback(async (cid) => {
    try { await api.post(`/conversations/${cid}/read`); } catch {}
  }, []);

  const openConversation = useCallback(async (cid) => {
    setActiveId(cid); activeRef.current = cid;
    await loadMessages(cid);
    await markRead(cid);
    loadConversations();
  }, [loadMessages, markRead, loadConversations]);

  const closeConversation = useCallback(() => {
    setActiveId(null); activeRef.current = null; setMessages([]);
  }, []);

  const handleWs = useCallback((evt) => {
    if (evt.type === "message") {
      const cid = evt.conversation_id;
      if (cid === activeRef.current) {
        setMessages((p) => p.some((m) => m.message_id === evt.message.message_id) ? p : [...p, evt.message]);
        markRead(cid);
      }
      loadConversations();
    } else if (evt.type === "typing") {
      setTyping((p) => ({ ...p, [evt.conversation_id]: { name: evt.user_name, at: Date.now() } }));
    } else if (evt.type === "taxonomy_updated") {
      reloadOptions();
    }
  }, [loadConversations, markRead, reloadOptions]);

  useEffect(() => {
    if (!user) return;
    let closed = false;
    const connect = () => {
      const url = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (e) => { try { handleWs(JSON.parse(e.data)); } catch {} };
      ws.onclose = () => { setConnected(false); if (!closed) reconnectRef.current = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => { closed = true; clearTimeout(reconnectRef.current); try { wsRef.current?.close(); } catch {} };
  }, [user, handleWs]);

  useEffect(() => {
    if (!user) return;
    loadConversations();
    const t = setInterval(loadConversations, 10000);
    return () => clearInterval(t);
  }, [user, loadConversations]);

  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => loadMessages(activeId), 5000);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  useEffect(() => {
    const t = setInterval(() => setTyping((p) => {
      const now = Date.now(); const next = {};
      Object.entries(p).forEach(([k, v]) => { if (now - v.at < 4000) next[k] = v; });
      return Object.keys(next).length === Object.keys(p).length ? p : next;
    }), 1500);
    return () => clearInterval(t);
  }, []);

  const sendMessage = useCallback(async (text, attachments = []) => {
    const cid = activeRef.current; if (!cid) return;
    const { data } = await api.post(`/conversations/${cid}/messages`, { text, attachments });
    setMessages((p) => p.some((m) => m.message_id === data.message_id) ? p : [...p, data]);
    loadConversations();
  }, [loadConversations]);

  const sendTyping = useCallback(() => {
    const cid = activeRef.current; if (!cid) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "typing", conversation_id: cid }));
  }, []);

  const startDm = useCallback(async (userId) => {
    const { data } = await api.post("/conversations/dm", { user_id: userId });
    await loadConversations(); openConversation(data.conversation_id); return data;
  }, [loadConversations, openConversation]);
  const createChannel = useCallback(async (name) => {
    const { data } = await api.post("/conversations/channel", { name });
    await loadConversations(); openConversation(data.conversation_id); return data;
  }, [loadConversations, openConversation]);
  const startPartner = useCallback(async (orgId) => {
    const { data } = await api.post("/conversations/partner", { org_id: orgId });
    await loadConversations(); openConversation(data.conversation_id); return data;
  }, [loadConversations, openConversation]);

  const unreadTotal = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  const active = conversations.find((c) => c.conversation_id === activeId) || null;

  return (
    <Ctx.Provider value={{
      conversations, unreadTotal, activeId, active, messages, othersLastRead, typing, connected,
      openConversation, closeConversation, sendMessage, sendTyping, startDm, createChannel, startPartner, loadConversations,
    }}>
      {children}
    </Ctx.Provider>
  );
};
